import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import axios from 'axios';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, updateData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import CopyrightIcon from '@mui/icons-material/Copyright';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { Tooltip } from '@mui/material';
import {
    Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TableFooter
} from '@mui/material';
import Modal from '@mui/material/Modal';
import { Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useLocation, Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function SalesDetailsMonthly() {
    const { isAuthenticated, companyId, branchId, permissions, token, username, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        // Token is not expired, fetch data
        fetchTableData();

    }, [isAuthenticated, companyId, branchId, permissions, token, logout]);

    const [data, setData] = useState([]);
    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]); // Default to today's date
    const [isFocused, setIsFocused] = useState(false);
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const hasReadReportPermission = permissions.includes('Read Reports');

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleToDateChange = (event) => {
        setToDate(event.target.value); // Ensure `toDate` is updated
        setData([]);
        //fetchTableData();
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //handle search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    //search
    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (!toDate) {
            console.error('To Date is required!');
            return;
        }
        if (hasReadReportPermission) {
            setData([]);
            setLoading(true);
            let bid = branchId;
            if (selectedBranch) {
                bid = selectedBranch.value;
            }
            let categoryid = 0;
            if (selectedStyle) {
                categoryid = selectedStyle.value;
            }
            try {
                const url = `/SalesMonthly/${toDate}/${toDate}/${companyId}/${bid}/${categoryid}`;
                const response = await fetchData('ReportApi', url, token);
                if (response.responseCode === '1111') {
                    setData(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Sales Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Monthly Sales");

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const title = `Monthly Sales Report: To ${formatDate(toDate)} | Generated On: ${formattedDateTime}`;

        // Title Row
        worksheet.mergeCells('A1:O1'); // 15 columns (Code, Brand, Item, 12 months)
        const titleCell = worksheet.getCell('A1');
        titleCell.value = title;
        titleCell.font = { bold: true, size: 11 };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

        // Month headers
        const monthHeaders = getLastOneYear(toDate);
        const headerRow = worksheet.addRow(['Code', 'Brand', 'Item', ...monthHeaders]);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCE5FF' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        // 🔹 Group data by StyleName
        const groupedData = filteredData.reduce((acc, row) => {
            if (!acc[row.StyleName]) acc[row.StyleName] = [];
            acc[row.StyleName].push(row);
            return acc;
        }, {});

        // 🔹 Track grand totals
        const grandTotals = Array(12).fill(0);
        const lastColumn = 14;
        // 🔹 Loop over groups
        Object.entries(groupedData).forEach(([styleName, rows]) => {
            // Group Header
            const groupHeader = worksheet.addRow([styleName]);
            groupHeader.font = { bold: true, color: { argb: 'FF08396B' } };
            //groupHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
            // ✅ Apply fill only to A..J

            for (let i = 1; i <= lastColumn; i++) {
                const cell = groupHeader.getCell(i);
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFE0E0E0" }, // light green
                };
            }

            groupHeader.alignment = { horizontal: 'left' };
            worksheet.mergeCells(`A${groupHeader.number}:O${groupHeader.number}`);

            // Reset group totals
            const groupTotals = Array(12).fill(0);

            // Items
            rows.forEach((row) => {
                const values = [
                    row.ItemCode,
                    row.BrandName,
                    row.ItemName,
                    row.Month1Quantity,
                    row.Month2Quantity,
                    row.Month3Quantity,
                    row.Month4Quantity,
                    row.Month5Quantity,
                    row.Month6Quantity,
                    row.Month7Quantity,
                    row.Month8Quantity,
                    row.Month9Quantity,
                    row.Month10Quantity,
                    row.Month11Quantity,
                    row.Month12Quantity,
                ];
                worksheet.addRow(values);

                // Update group + grand totals
                values.slice(3).forEach((val, i) => {
                    groupTotals[i] += val || 0;
                    grandTotals[i] += val || 0;
                });
            });

            // Group Total Row
            const totalRow = worksheet.addRow(['', '', 'Total', ...groupTotals]);
            totalRow.font = { bold: true, color: { argb: 'FF8B0000' } };
            totalRow.eachCell((cell) => {
                cell.alignment = { horizontal: 'right' };
            });
        });

        // 🔹 Grand Total Row
        const grandTotalRow = worksheet.addRow(['Grand Total', '', '', ...grandTotals]);
        grandTotalRow.font = { bold: true, color: { argb: 'FF08396B' } };
        grandTotalRow.eachCell((cell) => {
            cell.alignment = { horizontal: 'right' };
        });

        for (let i = 1; i <= lastColumn + 1; i++) {
            const cell = grandTotalRow.getCell(i);
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFE0E0E0" }, // light green
            };
        }

        // Column widths
        worksheet.columns = [
            { width: 15 }, { width: 20 }, { width: 30 },
            { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 },
            { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 },
        ];

        // Save file
        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `MonthlySales_${formatDate(toDate).replace(/\//g, '-')}.xlsx`;
        saveAs(new Blob([buffer]), fileName);
    };

    
    // Utility to get last 12 months with year (e.g., OCT-24)
    const getLastOneYear = (toDate) => {
        const months = [];
        const end = new Date(toDate);

        for (let i = 11; i >= 0; i--) {
            const d = new Date(end.getFullYear(), end.getMonth() - i, 1);
            const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase(); // e.g., "OCT"
            const year = String(d.getFullYear()).slice(-2); // e.g., "24"
            months.push(`${month}-${year}`);
        }

        return months;
    };


    useEffect(() => {
        fetchStyleData();
        fetchBranchData();
    }, []);

    //FOR GET Style  Details FROM API
    const [StyleOptions, setStyleOptions] = useState([]);
    const [selectedStyle, setSelectedStyle] = useState(null);

    const fetchStyleData = () => {
        fetchData('StyleApi', `/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                // Transform the data into the format that react-select expects (label and value)                
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.Id,
                }));
                setStyleOptions(options);

            })
            .catch((error) => {
                console.error('Error fetching Customer data:', error);
            });
    };

    const [BranchOptions, setBranchOptions] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    //FOR GET Consignee  Details FROM API
    const fetchBranchData = () => {
        fetchData('BranchApi', `/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                // Transform the data into the format that react-select expects (label and value)                
                const options = data.map((Consignee) => ({
                    label: Consignee.Name,
                    value: Consignee.Id,
                }));
                setBranchOptions(options);

            })
            .catch((error) => {
                console.error('Error fetching Consignee data:', error);
            });
    };

    const handleBranchSelect = (selectedOption) => {
        setSelectedBranch(selectedOption);
        if (!selectedOption) return;
    };

    const handleStyleChange = (selectedOption) => {
        setSelectedStyle(selectedOption);
        if (!selectedOption) return;
    };



    return (

        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>

            <div className="flex items-center gap-4 w-full" style={{ marginTop: '10px' }}>

                <h6
                    className="block uppercase tracking-wide text-black productHead flex-grow linkcolor"
                    style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', color: 'black' }}
                >
                    Monthly Sales
                </h6>

                {/* Branch */}
                <div className="flex items-center gap-2" style={{ marginTop: '20px' }}>
                    <label className="uppercase text-black text-xs whitespace-nowrap">Branch</label>
                    <Select
                        value={selectedBranch}
                        onChange={handleBranchSelect}
                        options={BranchOptions}
                        placeholder="Select"
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "35px",
                                height: "35px",
                                fontSize: "14px",
                                width: 270,
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "35px",
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "35px",
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                {/* Style */}
                <div className="flex items-center gap-2" style={{ marginTop: '20px' }}>
                    <label className="uppercase text-black text-xs whitespace-nowrap">Style</label>
                    <Select
                        value={selectedStyle}
                        onChange={handleStyleChange}
                        options={StyleOptions}
                        placeholder="Select"
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "35px",
                                height: "35px",
                                fontSize: "14px",
                                width: 270,
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "35px",
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "35px",
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                {/* To Date */}
                <div style={{ maxWidth: '150px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" style={{ fontFamily: 'monospace' }}>
                        To Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date"
                        value={toDate}
                        onChange={handleToDateChange}
                        style={{ marginTop: '-10px', width: '100%' }}
                    />
                </div>

                {/* Refresh */}
                <Button
                    style={{
                        background: 'linear-gradient(to right, #C7FFD8,#C7FFD8)',
                        borderRadius: '50px',
                        padding: '10px 20px',
                        color: '#0F0F0F',
                        fontWeight: 'bolder',
                        textTransform: 'none',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        maxHeight: '35px',
                        marginTop: '15px'
                    }}
                    onClick={fetchTableData}
                    className="linkcolor"
                >
                    <span>Refresh</span>
                </Button>

                {/* Excel */}
                <Button
                    style={{
                        background: 'linear-gradient(to right, #98DED9,#98DED9)',
                        borderRadius: '50px',
                        padding: '10px 20px',
                        color: '#0F0F0F',
                        fontWeight: 'bolder',
                        textTransform: 'none',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        maxHeight: '35px',
                        marginTop: '15px'
                    }}
                    onClick={handleExportExcel}
                    className="linkcolor"
                >
                    <span>Excel</span>
                </Button>
            </div>


            <div className="watermark" style={{ position: 'absolute', bottom: '10px', right: '20px' }}>
                <CopyrightIcon />
                <b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
            </div>


            <div
                className="w-full mb-5 flex"
                style={{ maxHeight: '550px', overflowY: 'auto', marginTop: '7px' }}
            >
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#08396B', width: '2500px' }}>
                            <TableRow sx={{ whiteSpace: 'nowrap' }}>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#27445D',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: 'bolder',
                                        fontSize: '14px',
                                        padding: '5px',
                                        color: 'whitesmoke',
                                        textTransform: 'uppercase',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    Code
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#27445D',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: 'bolder',
                                        fontSize: '14px',
                                        padding: '5px',
                                        color: 'whitesmoke',
                                        textTransform: 'uppercase',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    Brand
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#27445D',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: 'bolder',
                                        fontSize: '14px',
                                        padding: '5px',
                                        color: 'whitesmoke',
                                        textTransform: 'uppercase',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    Item
                                </TableCell>
                                {getLastOneYear(toDate).map((month, idx) => (
                                    <TableCell
                                        key={idx}
                                        sx={{
                                            backgroundColor: '#27445D',
                                            borderBottom: '1px solid #ddd',
                                            fontWeight: 'bolder',
                                            fontSize: '14px',
                                            padding: '5px',
                                            color: 'whitesmoke',
                                            textTransform: 'uppercase',
                                            fontFamily: 'monospace',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {month}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody sx={{ backgroundColor: 'whitesmoke' }}>
                            {(() => {
                                // 🔹 Step 1: Group data by StyleName
                                const groupedData = filteredData.reduce((acc, row) => {
                                    if (!acc[row.StyleName]) acc[row.StyleName] = [];
                                    acc[row.StyleName].push(row);
                                    return acc;
                                }, {});

                                // 🔹 Step 2: Compute overall grand totals
                                const grandTotals = {
                                    Month1: filteredData.reduce((sum, r) => sum + (r.Month1Quantity || 0), 0),
                                    Month2: filteredData.reduce((sum, r) => sum + (r.Month2Quantity || 0), 0),
                                    Month3: filteredData.reduce((sum, r) => sum + (r.Month3Quantity || 0), 0),
                                    Month4: filteredData.reduce((sum, r) => sum + (r.Month4Quantity || 0), 0),
                                    Month5: filteredData.reduce((sum, r) => sum + (r.Month5Quantity || 0), 0),
                                    Month6: filteredData.reduce((sum, r) => sum + (r.Month6Quantity || 0), 0),
                                    Month7: filteredData.reduce((sum, r) => sum + (r.Month7Quantity || 0), 0),
                                    Month8: filteredData.reduce((sum, r) => sum + (r.Month8Quantity || 0), 0),
                                    Month9: filteredData.reduce((sum, r) => sum + (r.Month9Quantity || 0), 0),
                                    Month10: filteredData.reduce((sum, r) => sum + (r.Month10Quantity || 0), 0),
                                    Month11: filteredData.reduce((sum, r) => sum + (r.Month11Quantity || 0), 0),
                                    Month12: filteredData.reduce((sum, r) => sum + (r.Month12Quantity || 0), 0),
                                };


                                return (
                                    <>
                                        {Object.entries(groupedData).map(([styleName, rows]) => {
                                            // 🔹 Step 3: Compute subtotal for this style
                                            const totals = {
                                                Month1: rows.reduce((sum, r) => sum + (r.Month1Quantity || 0), 0),
                                                Month2: rows.reduce((sum, r) => sum + (r.Month2Quantity || 0), 0),
                                                Month3: rows.reduce((sum, r) => sum + (r.Month3Quantity || 0), 0),
                                                Month4: rows.reduce((sum, r) => sum + (r.Month4Quantity || 0), 0),
                                                Month5: rows.reduce((sum, r) => sum + (r.Month5Quantity || 0), 0),
                                                Month6: rows.reduce((sum, r) => sum + (r.Month6Quantity || 0), 0),
                                                Month7: rows.reduce((sum, r) => sum + (r.Month7Quantity || 0), 0),
                                                Month8: rows.reduce((sum, r) => sum + (r.Month8Quantity || 0), 0),
                                                Month9: rows.reduce((sum, r) => sum + (r.Month9Quantity || 0), 0),
                                                Month10: rows.reduce((sum, r) => sum + (r.Month10Quantity || 0), 0),
                                                Month11: rows.reduce((sum, r) => sum + (r.Month11Quantity || 0), 0),
                                                Month12: rows.reduce((sum, r) => sum + (r.Month12Quantity || 0), 0),
                                            };

                                            return (
                                                <React.Fragment key={styleName}>
                                                    {/* Group Header */}
                                                    <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                                                        <TableCell
                                                            colSpan={15}
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                fontSize: '12px',
                                                                padding: '6px',
                                                                color: '#08396B',
                                                                textTransform: 'uppercase',
                                                                border: '1px solid #f0ebebff',
                                                            }}
                                                        >
                                                            {styleName}
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Items */}
                                                    {rows.map((row, index) => (
                                                        <TableRow key={index} sx={{ backgroundColor: 'white' }}>
                                                            <TableCell sx={{ border: '1px solid #ddd', padding: '3px', fontSize: '13px' }}>
                                                                {row.ItemCode}
                                                            </TableCell>
                                                            <TableCell sx={{ border: '1px solid #ddd', padding: '3px', fontSize: '12px' }}>
                                                                {row.BrandName}
                                                            </TableCell>
                                                            <TableCell sx={{ border: '1px solid #ddd', padding: '3px', fontSize: '12px' }}>
                                                                {row.ItemName}
                                                            </TableCell>
                                                            {[row.Month1Quantity, row.Month2Quantity, row.Month3Quantity, row.Month4Quantity, row.Month5Quantity, row.Month6Quantity, row.Month7Quantity, row.Month8Quantity, row.Month9Quantity, row.Month10Quantity, row.Month11Quantity, row.Month12Quantity].map(
                                                                (val, i) => (
                                                                    <TableCell
                                                                        key={i}
                                                                        sx={{
                                                                            border: '1px solid #ddd',
                                                                            padding: '3px',
                                                                            fontSize: '13px',
                                                                            textAlign: 'center',
                                                                        }}
                                                                    >
                                                                        {val}
                                                                    </TableCell>
                                                                )
                                                            )}
                                                        </TableRow>
                                                    ))}

                                                    {/* Group Total */}
                                                    <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                                        <TableCell sx={{ border: '1px solid #f8f7f7ff' }}></TableCell>
                                                        <TableCell sx={{ border: '1px solid #f8f7f7ff' }}></TableCell>
                                                        <TableCell
                                                            sx={{
                                                                border: '1px solid #f8f7f7ff',
                                                                fontWeight: 'bold',
                                                                color: 'darkred',
                                                                fontSize: '13px',
                                                                padding: '3px',
                                                            }}
                                                        >
                                                            Total
                                                        </TableCell>
                                                        {[totals.Month1, totals.Month2, totals.Month3, totals.Month4, totals.Month5, totals.Month6, totals.Month7, totals.Month8, totals.Month9, totals.Month10, totals.Month11, totals.Month12].map((val, i) => (
                                                            <TableCell
                                                                key={i}
                                                                sx={{
                                                                    border: '1px solid #f8f7f7ff',
                                                                    fontWeight: 'bold',
                                                                    color: 'darkred',
                                                                    fontSize: '13px',
                                                                    textAlign: 'center',
                                                                    padding: '3px',
                                                                }}
                                                            >
                                                                {val}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })}

                                        {/* 🔹 Grand Total */}
                                        <TableRow sx={{ backgroundColor: 'white', border: '2px solid #0c0c0cff' }}>
                                            <TableCell
                                                sx={{
                                                    // border: '2px solid #f8f6f6ff',
                                                    fontWeight: 'bold',
                                                    color: '#08396B',
                                                    fontSize: '14px',
                                                    padding: '5px',
                                                }}
                                            >
                                                Grand Total
                                            </TableCell>
                                            <TableCell sx={{ border: '2px solid #f8f6f6ff' }}></TableCell>
                                            <TableCell sx={{ border: '2px solid #f8f6f6ff' }}></TableCell>
                                            {[grandTotals.Month1, grandTotals.Month2, grandTotals.Month3, grandTotals.Month4, grandTotals.Month5, grandTotals.Month6, grandTotals.Month7, grandTotals.Month8, grandTotals.Month9, grandTotals.Month10, grandTotals.Month11, grandTotals.Month12].map((val, i) => (
                                                <TableCell
                                                    key={i}
                                                    sx={{
                                                        //  border: '2px solid #f8f6f6ff',
                                                        fontWeight: 'bold',
                                                        color: '#08396B',
                                                        fontSize: '14px',
                                                        textAlign: 'center',
                                                        padding: '5px',
                                                    }}
                                                >
                                                    {val}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </>
                                );
                            })()}
                        </TableBody>

                    </Table>
                </TableContainer>
            </div>

        </div>
    );
}

export default SalesDetailsMonthly;
